import {
    type StatusOption,
    StatusPicker,
} from '@apps/web/components/ui/status_picker';
import {
    STATUS_INTENT_TOKENS,
    type StatusIntent,
    statusIntentBadgeClassName,
} from '@apps/web/components/ui/status_tokens';
import { cn } from '@apps/web/lib/utils';
import { type CaregiverResponseType } from '@libraries/typescript/api_client';

// The six outreach response statuses, shared by every place a caregiver's
// availability is shown or edited. Semantic intents style the trigger pill;
// solid dot tokens identify options in the dropdown.
const RESPONSE_OPTIONS: ReadonlyArray<StatusOption<CaregiverResponseType>> = [
    {
        value: 'available_interested',
        label: 'Available',
        dotColor: STATUS_INTENT_TOKENS.positive.dot,
    },
    {
        value: 'not_a_fit',
        label: 'Not a fit',
        dotColor: STATUS_INTENT_TOKENS.warning.dot,
    },
    {
        value: 'unavailable',
        label: 'Unavailable',
        dotColor: STATUS_INTENT_TOKENS.negative.dot,
    },
    {
        value: 'pending',
        label: 'Pending Response',
        dotColor: STATUS_INTENT_TOKENS.neutral.dot,
    },
    {
        value: 'no_response',
        label: 'No Response',
        dotColor: STATUS_INTENT_TOKENS.neutral.dot,
    },
    {
        value: 'unknown',
        label: 'Needs Review',
        dotColor: STATUS_INTENT_TOKENS.warning.dot,
    },
];

const OPTION_BY_VALUE = new Map(
    RESPONSE_OPTIONS.map((option) => [option.value, option] as const),
);
OPTION_BY_VALUE.set('available_not_interested', {
    value: 'available_not_interested',
    label: 'Not a fit',
    dotColor: STATUS_INTENT_TOKENS.warning.dot,
});

const RESPONSE_INTENTS: Record<CaregiverResponseType, StatusIntent> = {
    available_interested: 'positive',
    available_not_interested: 'warning',
    not_a_fit: 'warning',
    unavailable: 'negative',
    pending: 'neutral',
    no_response: 'neutral',
    unknown: 'warning',
};

type ResponsePickerProps = {
    readonly value: CaregiverResponseType | null;
    readonly onSelect?: (value: CaregiverResponseType) => void;
    readonly disabled?: boolean;
    readonly ariaLabel?: string;
    // When `value` is null, render this option's pill as a display default
    // (not marked selected). Used where an empty response reads as "Pending".
    readonly defaultDisplayValue?: CaregiverResponseType;
    // Optional three-way editing: clicking the recorded value resets to it,
    // clicking the active value clears it. When these are omitted the picker is
    // a plain single-select.
    readonly recordedValue?: CaregiverResponseType | null;
    readonly onReset?: () => void;
    readonly onClear?: () => void;
};

/**
 * The single outreach response status control. Renders a colored status pill and
 * a searchable dropdown of the six response options. Supports both plain
 * selection and three-way (select / reset-to-recorded / clear) editing.
 */
export function ResponsePicker({
    value,
    onSelect,
    disabled = false,
    ariaLabel,
    defaultDisplayValue,
    recordedValue,
    onReset,
    onClear,
}: ResponsePickerProps) {
    const displayValueKey = value ?? defaultDisplayValue ?? null;
    const displayOption = displayValueKey
        ? (OPTION_BY_VALUE.get(displayValueKey) ?? null)
        : null;

    const triggerClassName = displayOption
        ? cn(
              'rounded-full border px-2 py-0.5',
              statusIntentBadgeClassName(RESPONSE_INTENTS[displayOption.value]),
          )
        : 'rounded-full border px-2 py-0.5 text-muted-foreground hover:bg-muted';

    const displayNode = displayOption ? (
        <span className="inline-flex items-center gap-1.5">
            <span
                className={cn(
                    'h-1.5 w-1.5 rounded-full',
                    displayOption.dotColor,
                )}
            />
            <span>{displayOption.label}</span>
        </span>
    ) : undefined;

    const handleSelect = (selected: CaregiverResponseType) => {
        if (onClear && value === selected) {
            onClear();
            return;
        }
        if (onReset && recordedValue === selected) {
            onReset();
            return;
        }
        onSelect?.(selected);
    };

    return (
        <StatusPicker
            options={RESPONSE_OPTIONS}
            value={value}
            onSelect={handleSelect}
            disabled={disabled}
            displayValue={displayNode}
            placeholder="—"
            triggerClassName={triggerClassName}
            contentClassName="w-[200px]"
            contentSide="left"
            searchable={true}
            searchPlaceholder="Search status..."
            ariaLabel={ariaLabel}
        />
    );
}
